/**
 * Shared firm-thread list (#542 slice 2) — the one thread-row + expanded-
 * thread + reply-composer rendering, used by BOTH message surfaces: the
 * case-detail panel's "Messages" section (slice 1) and the global Messages
 * view (slice 2). Extracted from `case-messages.tsx` so the operator reads
 * the same row everywhere; the global view adds only the muted case-label
 * chip (`showCaseChip`) for wayfinding across matters.
 *
 * Colour law (ADR-0134): the ONE hue spent here is the unread cue — an unread
 * firm message is a standing "look/examine" demand, so it wears amber (the
 * examine anchor), paired with subject weight so colour is never the sole
 * signal. Everything else — firm name, dates, the case-label and Direct chips
 * — is a fact and stays muted (ADR-0132 posture). The interactive blue lane
 * is NOT spent on unread (one hue, one meaning — Q-LANE).
 *
 * Renderer guard (ADR-0071 Amendment): firm-authored `body` renders as
 * auto-escaped JSX text (never HTML), and authorship renders from the door's
 * STRUCTURED author fields — never parsed out of the body string.
 *
 * Read receipts: expanding an unread thread marks it read-by-LF (once per
 * mount, retried on failure), clearing the same flag the portal's admin inbox
 * reads — intended: CC is the working surface (#542 decision 1). Replying
 * emails the firm and mirrors to Slack (portal-side); the composer says so.
 */

import { useRef, useState } from "react";
import type { FirmThread } from "@command-center/shared";
import { replySavedOf, useMarkThreadRead, useReplyToThread } from "@/lib/use-firm-messages";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

export function FirmThreadList({
  threads,
  testId,
  showCaseChip = false,
}: {
  threads: FirmThread[];
  testId: string;
  /** Global view: show the case-label chip on case-tagged rows. The case
   *  panel omits it — the matter is already the context. */
  showCaseChip?: boolean;
}) {
  const markRead = useMarkThreadRead();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Threads already marked this mount — expanding twice must not re-PATCH.
  const marked = useRef(new Set<string>());

  const toggle = (thread: FirmThread) => {
    const opening = expandedId !== thread.id;
    setExpandedId(opening ? thread.id : null);
    if (opening && thread.unread && !marked.current.has(thread.id)) {
      // Add before the call so a rapid double-toggle can't double-PATCH;
      // remove on failure so the NEXT expand retries (a permanently-stuck
      // unread dot was the #542 review's sticky-desync finding).
      marked.current.add(thread.id);
      markRead.mutate(
        { threadId: thread.id },
        { onError: () => marked.current.delete(thread.id) },
      );
    }
  };

  return (
    <ul className="flex flex-col gap-1" data-testid={testId}>
      {threads.map((thread) => (
        <li key={thread.id} className="flex flex-col">
          <button
            type="button"
            onClick={() => toggle(thread)}
            aria-expanded={expandedId === thread.id}
            data-testid={`${testId}-thread`}
            className="-mx-2 flex flex-col gap-0.5 rounded px-2 py-1.5 text-left hover:bg-accent"
          >
            <span className="flex items-center gap-2">
              {thread.unread ? (
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning"
                  aria-hidden="true"
                  data-testid={`${testId}-unread-dot`}
                />
              ) : null}
              <span
                className={`text-sm ${thread.unread ? "font-medium" : "text-foreground/90"}`}
              >
                {thread.subject || "(no subject)"}
              </span>
              {thread.unread ? <span className="sr-only">Unread</span> : null}
              {showCaseChip && thread.case_label ? (
                <Badge variant="muted" data-testid={`${testId}-case-chip`}>
                  {thread.case_label}
                </Badge>
              ) : null}
              {thread.visibility === "Direct" ? (
                <Badge variant="muted">Direct</Badge>
              ) : null}
            </span>
            <span className="text-xs text-muted-foreground">
              {thread.firm_name || "Unknown firm"} · {thread.messages.length}{" "}
              {thread.messages.length === 1 ? "message" : "messages"} · last{" "}
              {fmtDate(thread.last_activity, { year: true })}
            </span>
          </button>
          {expandedId === thread.id ? (
            <ThreadDetail thread={thread} testId={testId} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The expanded thread — messages ascending (root first) + the reply composer. */
function ThreadDetail({ thread, testId }: { thread: FirmThread; testId: string }) {
  const reply = useReplyToThread();
  const [draft, setDraft] = useState("");

  const submit = () => {
    const body = draft.trim();
    if (body === "" || reply.isPending) return;
    reply.mutate(
      { threadId: thread.id, body },
      { onSuccess: () => setDraft("") },
    );
  };

  return (
    <div
      className="mb-2 ml-2 flex flex-col gap-3 border-l pl-3 pt-1"
      data-testid={`${testId}-detail`}
    >
      {thread.messages.map((m) => (
        <div key={m.id} className="flex flex-col gap-0.5" data-testid={`${testId}-message`}>
          {/* Authorship from the STRUCTURED fields only (ADR-0071 guard). */}
          <span className="text-xs text-muted-foreground">
            {m.author_display_name || m.author_email || m.author_role}
            {" · "}
            {fmtDate(m.created_at, { year: true })}
          </span>
          <p className="whitespace-pre-wrap text-sm text-foreground">{m.body}</p>
        </div>
      ))}
      <div className="flex flex-col gap-1">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Reply to the firm…"
            className="w-full resize-none rounded border bg-background px-2 py-1 text-sm"
            data-testid={`${testId}-reply-draft`}
          />
          <button
            type="button"
            onClick={submit}
            disabled={reply.isPending || draft.trim() === ""}
            data-testid={`${testId}-reply-send`}
            className="shrink-0 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {reply.isPending ? "Sending…" : "Send reply"}
          </button>
        </div>
        <span className="text-[11px] text-muted-foreground">
          Emails the firm and posts to the Slack thread.
        </span>
        {reply.isError ? (
          <p className="text-[11px] text-error" data-testid={`${testId}-reply-error`}>
            {replySavedOf(reply.error) === false
              ? "Nothing was saved; it can be sent again."
              : "It may have saved. Read the thread before sending it again."}
          </p>
        ) : null}
      </div>
    </div>
  );
}
